import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { WebFormParams } from '../index';
import { WfFormRef } from '../components/wf-form';
import { WfSelectProps } from '../components/wf-select';
import { UploadedFile } from '../components/wf-upload';

import { CaseFormModel, FormIsDeleted } from '@/models/case-form.model';
import { FieldOptionType, FieldPermissionEnum, FieldWithOptionType } from '@/models/case-field.model';
import { WebformLanguage } from '@/models/web-form.model';
import { CaseDetailType } from '@/models/case-process.model';
import { FieldComponentTypeEnum } from '@/models/component-type.model';

import { formatDate, formatDateToUTC } from '@/utils/helper';
import { DEFAULT_LANGUAGE } from '@/config/i18n';

type WebFormConfigType = {
  case_forms: CaseFormModel[];
  with_options: FieldWithOptionType[];
  disable_attachment: boolean;
  disable_captcha: boolean;
  language: WebformLanguage;
  i18n_resource: Record<string, string>;
  show_message_at_bottom: boolean;
};

enum PageStatusEnum {
  NORMAL = 'NORMAL',
  LOADING = 'LOADING',
  SUBMIT_SUCCESS = 'SUBMIT_SUCCESS',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNAVAILABLE_ERROR = 'UNAVAILABLE_ERROR',
}

enum SubmitStatusEnum {
  NORMAL = 'NORMAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
}

enum CaptchaStatusEnum {
  NORMAL = 'NORMAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
}

function useWebForm(props: WebFormParams) {
  const {
    isPreview,
    formList = [],
    withOptions = [],
    disableAttachment: _disableAttachment = false,
    disableCaptcha: _disableCaptcha = false,
    language: _language = DEFAULT_LANGUAGE,
    i18nResource: _i18nResource = {},
  } = props;

  const submitErrorTimerId = useRef<NodeJS.Timeout>();
  const captchaTimerId = useRef<NodeJS.Timeout>();
  const formRef = useRef<WfFormRef<CaseDetailType>>(null);

  const [curPageStatus, setCurPageStatus] = useState(isPreview ? PageStatusEnum.NORMAL : PageStatusEnum.LOADING);
  const [curSubmitStatus, setCurSubmitStatus] = useState(SubmitStatusEnum.NORMAL);
  const [curCaptchaStatus, setCurCaptchaStatus] = useState(
    isPreview ? CaptchaStatusEnum.NORMAL : CaptchaStatusEnum.LOADING,
  );
  const [allFormList, setAllFormList] = useState<CaseFormModel[]>([]);
  const [allWithOptions, setAllWithOptions] = useState<FieldWithOptionType[]>([]);
  const [disableAttachment, setDisableAttachment] = useState(false);
  const [disableCaptcha, setDisableCaptcha] = useState(false);
  const [language, setLanguage] = useState<WebformLanguage>(DEFAULT_LANGUAGE);
  const [i18nResource, setI18nResource] = useState<Record<string, string>>({});
  const [showMessageAtBottom, setShowMessageAtBottom] = useState(false);
  const [curForm, setCurForm] = useState<CaseFormModel>();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [captchaImgSrc, setCaptchaImgSrc] = useState('');

  const showWebFormPage = curPageStatus === PageStatusEnum.NORMAL;
  const showLoading = curPageStatus === PageStatusEnum.LOADING;
  const showSubmitSuccessPage = curPageStatus === PageStatusEnum.SUBMIT_SUCCESS;
  const showNetworkErrorPage = curPageStatus === PageStatusEnum.NETWORK_ERROR;
  const showUnavailablePage = curPageStatus === PageStatusEnum.UNAVAILABLE_ERROR;
  const captchaLoading = curCaptchaStatus === CaptchaStatusEnum.LOADING;

  const t = useCallback(
    (_key: string, _i18nResource?: WebFormConfigType['i18n_resource']) => {
      const i18nResourceData = _i18nResource || i18nResource;
      const reg = /^webform\./;

      if (reg.test(_key)) {
        return i18nResourceData[_key.replace(reg, '')] || _key;
      }

      return i18nResourceData[_key] || _key;
    },
    [i18nResource],
  );

  const convertedFormList = useMemo(
    () =>
      allFormList.reduce<CaseFormModel[]>((prev, cur) => {
        if (!cur.is_edit_contact || cur.is_deleted !== FormIsDeleted.Normal) return prev;

        prev.push({
          ...cur,
          shown_contact: cur.create_by === 'system' ? t(cur.shown_contact) : cur.shown_contact,
          field_lists: cur.field_lists.map((field) => ({
            ...field,
            contact_display_name: field.is_system ? t(field.contact_display_name) : field.contact_display_name,
          })),
        });

        return prev;
      }, []),
    [allFormList, t],
  );

  const curWithOptions: FieldWithOptionType[] = useMemo(() => {
    if (!curForm || allWithOptions.length === 0) return [];

    const emailWithOption: FieldWithOptionType = {
      id: 'email',
      key: 'email',
      label: t('webform.email'),
      filter_type: FieldComponentTypeEnum.Dropdown,
      component_type: FieldComponentTypeEnum.Text,
      agent_required_create_or_update: false,
      agent_required_submit: false,
      is_system: true,
      is_case_bulk_update: false,
      is_case_properties: false,
      is_view_filters: false,
      options: [],
    };

    const shownComponentTypes = [
      FieldComponentTypeEnum.Dropdown,
      FieldComponentTypeEnum.DependentField,
      FieldComponentTypeEnum.MultiSelect,
      FieldComponentTypeEnum.Text,
      FieldComponentTypeEnum.MultiLineText,
      FieldComponentTypeEnum.Numeric,
      FieldComponentTypeEnum.Decimal,
      FieldComponentTypeEnum.Checkbox,
      FieldComponentTypeEnum.Date,
    ];

    return curForm.field_lists.reduce<FieldWithOptionType[]>(
      (prev, cur) => {
        if (cur.key === 'form_id') return prev;
        if (!shownComponentTypes.includes(cur.component_type)) return prev;
        if (cur.permission !== FieldPermissionEnum.EDITABLE_FOR_CONTACTS) return prev;

        const curWithOption = allWithOptions.find((option) => option.id === cur.id);
        if (curWithOption) {
          prev.push({
            ...curWithOption,
            options: curWithOption.options.reduce<FieldOptionType[]>((prev, cur) => {
              if (!cur.is_case_properties) return prev;

              prev.push({
                ...cur,
                label: cur.is_system ? t(cur.label) : cur.label,
              });

              return prev;
            }, []),
          });
        }
        return prev;
      },
      [emailWithOption],
    );
  }, [curForm, allWithOptions, t]);

  const formOptions = useMemo(
    () => convertedFormList.map((item) => ({ label: item.shown_contact, value: item.id })),
    [convertedFormList],
  );

  const postMsg = useCallback((type: string, data?: any) => {
    if (window === window.parent) {
      alert(
        `You can't use WebForm like this.\nPlease insert 'web-form/' before 'index.html' in the url.\nExample: xxx.xxx.com/case-management-ui/latest/web-form/index.html?web_form_key=xxxxxx`,
      );
    } else {
      window.parent.postMessage(JSON.stringify({ type, data }), '*');
    }
  }, []);

  const onMessageCallback = useCallback(async (e: MessageEvent<any>) => {
    try {
      const { type, data } = JSON.parse(e.data);

      switch (type) {
        case 'GET_WEB_FORM_TOKEN:UNAVAILABLE':
          setCurPageStatus(PageStatusEnum.UNAVAILABLE_ERROR);
          break;
        case 'GET_WEB_FORM_TOKEN:NETWORK_ERROR':
          setCurPageStatus(PageStatusEnum.NETWORK_ERROR);
          break;
        case 'GET_WEB_FORM_CONFIG:LOADING':
          setCurPageStatus(PageStatusEnum.LOADING);
          break;
        case 'GET_WEB_FORM_CONFIG:SUCCESS':
          const {
            case_forms,
            with_options,
            disable_attachment,
            disable_captcha,
            language: _language,
            i18n_resource,
            show_message_at_bottom,
          }: WebFormConfigType = data;

          setAllFormList(case_forms);
          setAllWithOptions(with_options);
          setDisableAttachment(disable_attachment);
          setDisableCaptcha(disable_captcha);
          setLanguage(_language);
          setI18nResource(i18n_resource);
          setShowMessageAtBottom(show_message_at_bottom);

          setCurPageStatus(PageStatusEnum.NORMAL);
          break;
        case 'GET_WEB_FORM_CONFIG:NETWORK_ERROR':
          setCurPageStatus(PageStatusEnum.NETWORK_ERROR);
          break;
        case 'GET_CAPTCHA:LOADING':
          captchaTimerId.current && clearTimeout(captchaTimerId.current);
          captchaTimerId.current = setTimeout(handleRefreshCaptcha, 1000 * 60 * 9);

          setCurCaptchaStatus(CaptchaStatusEnum.LOADING);
          break;
        case 'GET_CAPTCHA:SUCCESS':
          setCaptchaImgSrc(data);

          setCurCaptchaStatus(CaptchaStatusEnum.NORMAL);
          break;
        case 'GET_CAPTCHA:ERROR':
          setCurCaptchaStatus(CaptchaStatusEnum.ERROR);
          break;
        case 'SUBMIT:LOADING':
          setCurSubmitStatus(SubmitStatusEnum.LOADING);
          break;
        case 'SUBMIT:SUCCESS':
          setCurPageStatus(PageStatusEnum.SUBMIT_SUCCESS);
          break;
        case 'SUBMIT:CAPTCHA_ERROR':
          const errorOption = { message: 'Incorrect characters entered. Please try again.' };
          formRef.current?.setError('captcha_text', errorOption);

          setCurSubmitStatus(SubmitStatusEnum.NORMAL);
          break;
        case 'SUBMIT:ERROR':
          setCurSubmitStatus(SubmitStatusEnum.ERROR);
          submitErrorTimerId.current = setTimeout(() => {
            setCurSubmitStatus(SubmitStatusEnum.NORMAL);
          }, 3000);
          break;
      }
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initWebFormConfig = async () => {
    if (isPreview) {
      setAllFormList(formList);
      setAllWithOptions(withOptions);
      setDisableAttachment(_disableAttachment);
      setDisableCaptcha(_disableCaptcha);
      setLanguage(_language);
      setI18nResource(_i18nResource);
      setCurPageStatus(PageStatusEnum.NORMAL);
    } else {
      postMsg('GET_WEB_FORM_CONFIG');
    }
  };

  const handleRefresh = () => {
    initWebFormConfig();
  };

  const handleRefreshCaptcha = () => {
    postMsg('GET_CAPTCHA');
  };

  const handleChangeForm: WfSelectProps['onChange'] = (value) => {
    if (curForm?.id === value) return;

    const form = convertedFormList.find((item) => item.id === value);
    setCurForm(form);
    formRef.current?.reset({ form_id: form?.id });
  };

  const processDataField = (result: ReturnType<typeof processFormData>) => {
    Object.entries(result).forEach(([key, value]) => {
      const curWithOption = curWithOptions.find((item) => item.key === key);
      if (!curWithOption || curWithOption.is_system) return;

      Reflect.deleteProperty(result, key);
      switch (curWithOption.component_type) {
        case FieldComponentTypeEnum.Numeric:
        case FieldComponentTypeEnum.Decimal:
          result.data_field[key] = ['', undefined].includes(value) ? null : Number(value);
          break;
        case FieldComponentTypeEnum.Checkbox:
          result.data_field[key] = value || false;
          break;
        case FieldComponentTypeEnum.Date:
          result.data_field[key] = value ? `${formatDateToUTC(value)},${formatDate(value)}` : undefined;
          break;
        default:
          result.data_field[key] = value;
      }
    });
  };

  const processAttachmentIds = (result: ReturnType<typeof processFormData>) => {
    result.attachment_ids = uploadedFiles.reduce<string[]>((prev, cur) => {
      if (cur.status === 'Resolved' && !!cur.id) {
        prev.push(cur.id);
      }
      return prev;
    }, []);
  };

  const processFormData = (
    formData: CaseDetailType,
  ): CaseDetailType & { data_field: Record<string, unknown>; attachment_ids: string[] } => {
    const result = Object.assign(formData, {
      data_field: {} as Record<string, unknown>,
      attachment_ids: [] as string[],
    });

    processDataField(result);
    !setDisableAttachment && processAttachmentIds(result);

    return result;
  };

  const validateForm = async () => {
    if (!formRef.current) return false;
    if (formRef.current.formState.errors.captcha_text) return false;

    const valid = await formRef.current.trigger();
    if (valid) return true;

    const { errors } = formRef.current.formState;
    const errorWithOption = curWithOptions.find((item) => errors[item.key]);
    if (errorWithOption) {
      const el = document.getElementById(errorWithOption.id);
      el?.scrollIntoView({ behavior: 'smooth' });
    }

    return false;
  };

  const handleSubmit = async () => {
    if (isPreview || !formRef.current) return;
    if (!(await validateForm())) return;

    submitErrorTimerId.current && clearTimeout(submitErrorTimerId.current);

    const formData = processFormData(formRef.current.getValues());
    postMsg('SUBMIT', formData);
  };

  // Register the onMessage event
  useEffect(() => {
    if (isPreview) return;

    window.addEventListener('message', onMessageCallback);

    return () => {
      window.removeEventListener('message', onMessageCallback);
    };
  }, [isPreview, onMessageCallback]);

  // Use ResizeObserver to monitor changes in the height of the wfContainer element.
  // When the height changes, send a message to the parent window using postMessage
  // to dynamically adjust the height of the container.
  useEffect(() => {
    const wfContainerEle = document.getElementById('wfContainer');
    if (!wfContainerEle) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        postMsg('ADJUST_WF_CONTAINER_HEIGHT', newHeight);
      }
    });

    resizeObserver.observe(wfContainerEle);

    return () => {
      resizeObserver.unobserve(wfContainerEle);
    };
  }, [postMsg]);

  // Select unique form
  useEffect(() => {
    if (convertedFormList.length === 1 && formRef.current) {
      setCurForm(convertedFormList[0]);
      formRef.current.setValue('form_id', convertedFormList[0].id);
    }
  }, [convertedFormList]);

  // Init
  useEffect(() => {
    isPreview && initWebFormConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear timeout
  useEffect(() => {
    return () => {
      submitErrorTimerId.current && clearTimeout(submitErrorTimerId.current);
      captchaTimerId.current && clearTimeout(captchaTimerId.current);
    };
  }, []);

  return {
    formRef,
    curSubmitStatus,
    allFormList: convertedFormList,
    curForm,
    disableAttachment,
    disableCaptcha,
    language,
    showMessageAtBottom,
    captchaImgSrc,
    uploadedFiles,
    setUploadedFiles,
    showWebFormPage,
    showLoading,
    showSubmitSuccessPage,
    showNetworkErrorPage,
    showUnavailablePage,
    captchaLoading,
    curWithOptions,
    formOptions,
    t,
    handleRefresh,
    handleRefreshCaptcha,
    handleChangeForm,
    handleSubmit,
  };
}

export default useWebForm;
